import { type FC, memo, type ReactNode, useMemo } from "react";
import type { Key } from "react-aria";
import {
  HeaderDivider,
  HStack,
  Item,
  MenuOptionItem,
  MenuTriggerButton,
  notify,
  Section,
  Spacer,
  SymbolIcon,
  TriggerButton,
  useResizableTabPanelsContext,
} from "widgets";

import { getSequenceViewerSessionId } from "../../api/fetch.ts";
import { getUrls } from "../../api/openapi.ts";
import { refreshMain } from "../../main-canvas/render-main.ts";
import { getMaxRangeLength } from "../../main-canvas/utils.ts";
import { AlignmentDensityEnum } from "../../openapi";
import { MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import { routeWithBasename } from "../../utils/route-with-basename.ts";
import { TracksConfigurationDialog } from "../track-configuration/TracksConfiguration.tsx";
import style from "./GenomeViewerToolbar.module.css";
import { SaveViewDialog } from "./SaveViewDialog.tsx";

export const GenomeViewerToolbar = () => {
  const noTracks = useGenomeViewerStore((state) => state.tracks.size === 0);
  const { isOpen } = useResizableTabPanelsContext();

  if (noTracks) {
    return (
      <GenomeViewerToolbarContainer>
        <GenomeName />
        <Spacer />
        <HStack style={{ height: 40 }}>
          <ToggleInspectionPanel />
        </HStack>
      </GenomeViewerToolbarContainer>
    );
  }

  return (
    <GenomeViewerToolbarContainer>
      <HeaderDivider />
      <GenomeName />
      <Spacer />
      <SelectionMenu />
      <AlignmentMenu />
      <HeaderDivider />
      {!isOpen && (
        <>
          <TracksConfigurationDialog showLabel />
          <HeaderDivider />
        </>
      )}
      <SaveViewDialog />
      <HeaderDivider />
      <ToggleInspectionPanel />
    </GenomeViewerToolbarContainer>
  );
};

const SelectionMenu = () => {
  // Selection
  const fullRangeSelection = useGenomeViewerStore((state) => state.fullRangeSelection);
  const toggleFullRangeSelection = useGenomeViewerStore((state) => state.toggleFullRangeSelection);
  const getSelectedRange = useGenomeViewerStore((state) => state.getSelectedRange);
  const urls = useMemo(() => getUrls(), []);

  const handleAction = (key: Key) => {
    switch (key) {
      case "select-in-all-tracks": {
        toggleFullRangeSelection();
        break;
      }
      case "send-to-sequence-viewer": {
        {
          const rangeToSend = getSelectedRange();
          if (rangeToSend) {
            const maxRangeLength = getMaxRangeLength(rangeToSend);
            if (maxRangeLength <= MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER) {
              getSequenceViewerSessionId(rangeToSend).then((sessionId) => {
                window.open(routeWithBasename(`/visualizer/sequence?sessionId=${sessionId}`, urls), "_blank");
              });
            } else {
              // Selection is too wide
              notify.warning({
                title: "Selection Too Wide",
                subtitle: `The maximum allowed range is ${MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} bases.`,
                children: "You can zoom in or make a narrower selection using Shift + drag with your mouse.",
              });
            }
          } else {
            // No selection. Should never happen as we send the visible area then
            notify.warning({
              title: "No Selection Made",
              subtitle: "Please select a region to view the sequence.",
            });
          }
        }
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton icon="arrow_outward" label="Selection" onAction={handleAction} showLabel>
      <Section title="Selection">
        <Item key={"send-to-sequence-viewer"}>
          <MenuOptionItem
            icon="arrow_outward"
            title={"Open in Sequence Viewer"}
            description={`Size limited to ${MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} bp`}
          />
        </Item>
      </Section>
      <Section>
        <Item key="select-in-all-tracks">
          <MenuOptionItem
            title={"Select in All Tracks"}
            icon="select_all"
            tag={fullRangeSelection ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
      </Section>
    </MenuTriggerButton>
  );
};

const AlignmentMenu = () => {
  const density = useGenomeViewerStore((state) => state.alignmentDensity);
  const setDensity = useGenomeViewerStore((state) => state.setAlignmentDensity);
  const resetCanvas = useGenomeViewerStore((state) => state.resetAlignments);
  const centerAlignment = useGenomeViewerStore((state) => state.centerAlignment);
  const alignedFamily = useGenomeViewerStore((state) => state.alignedFamily);

  const handleAction = (key: Key) => {
    switch (key) {
      case "draw-alignments-single": {
        setDensity(density === AlignmentDensityEnum.SINGLE ? AlignmentDensityEnum.OFF : AlignmentDensityEnum.SINGLE);
        break;
      }
      case "draw-alignments-low": {
        setDensity(density === AlignmentDensityEnum.LOW ? AlignmentDensityEnum.OFF : AlignmentDensityEnum.LOW);
        break;
      }
      case "reset-alignments": {
        resetCanvas();
        break;
      }
      case "center-active-alignment": {
        centerAlignment();
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton
      icon="align_horizontal_left"
      label="Alignment"
      showLabel
      badge={!!alignedFamily}
      onAction={handleAction}
      disabledKeys={alignedFamily == undefined ? ["center-active-alignment", "reset-alignments"] : undefined}
    >
      <Section title="Alignment">
        <Item key="center-active-alignment">
          <MenuOptionItem title="Center Active Alignment" icon="align_horizontal_center" />
        </Item>
        <Item key="reset-alignments">
          <MenuOptionItem title="Reset Alignments" icon="reset_focus" />
        </Item>
      </Section>
      <Section>
        <Item key="draw-alignments-single">
          <MenuOptionItem
            title={"Active Alignment"}
            icon="pen_size_3"
            tag={density === AlignmentDensityEnum.SINGLE ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
        <Item key="draw-alignments-low">
          <MenuOptionItem
            title={"All Alignments"}
            icon="stroke_full"
            tag={density === AlignmentDensityEnum.LOW ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
      </Section>
    </MenuTriggerButton>
  );
};

export const GenomeViewerToolbarContainer: FC<{ children?: ReactNode }> = ({ children }) => {
  return (
    <HStack className={style.toolbar} data-testid="genome-viewer-toolbar">
      {children}
    </HStack>
  );
};

const GenomeName = memo(function LargeToolbar() {
  const loadedSyntenyOrganism = useGenomeViewerStore((state) => state.syntenyOrganism.name);

  return loadedSyntenyOrganism !== undefined && <div className={style.session_name}>{loadedSyntenyOrganism}</div>;
});

export const ToggleInspectionPanel = () => {
  const { isOpen, togglePanel } = useResizableTabPanelsContext();

  return (
    <TriggerButton
      onPress={() => {
        togglePanel();
      }}
      label="Toggle Inspection Panel"
      icon={<SymbolIcon icon={isOpen ? "right_panel_close" : "right_panel_open"} filled={isOpen} />}
      data-testid="toggle-inspection-panel"
    />
  );
};
